import type { HubBuyingOffer } from 'components/hub/useHubPurchaseOffers';
import type { HubSellingTransaction } from 'components/hub/useHubSellerTransactions';
import { isActiveBuyerOffer } from 'helpers/buyerOfferStatus';

import type { HomePortfolioRow } from './HomePortfolioTable';
import { buildOfferCardModel, buildOfferTransactionCardModel } from './offerCardModel';
import {
  buildPastSaleTransactionCardModel,
  buildSaleTransactionCardModel,
  isActiveSaleTransaction,
} from './saleTransactionCardModel';

export const TABLE_HOME_COUNT_THRESHOLD = 4;

const distinctAddressCount = (addressIds: ReadonlyArray<string | null | undefined>): number =>
  new Set(addressIds.filter((addressId) => addressId)).size;

export const usesHomeSalePortfolio = (transactions: HubSellingTransaction[]): boolean =>
  distinctAddressCount(transactions.map(({ address }) => address.id)) >= TABLE_HOME_COUNT_THRESHOLD;

export const salePortfolioRows = (transactions: HubSellingTransaction[]): HomePortfolioRow[] =>
  transactions.flatMap((transaction) => {
    const active = buildSaleTransactionCardModel(transaction);
    const model = active ?? buildPastSaleTransactionCardModel(transaction);
    if (!model) return [];
    const tableModel = !active && model.isComplete ? { ...model, isComplete: false } : model;
    return [
      {
        transaction: tableModel,
        street:
          [transaction.address.street, transaction.address.unitNumber].filter(Boolean).join(' ') ||
          'Your home sale',
        locality: [
          transaction.address.city,
          transaction.address.state,
          transaction.address.postalCode,
        ]
          .filter(Boolean)
          .join(', '),
        isActive: isActiveSaleTransaction(transaction),
      },
    ];
  });

export const purchasePortfolioRows = (offers: HubBuyingOffer[]): HomePortfolioRow[] =>
  offers.map((offer) => {
    const listing = offer.listing?.marketplaceListing;
    const transaction = buildOfferTransactionCardModel(buildOfferCardModel(offer));
    return {
      transaction:
        offer.transactionState === 'CLOSED'
          ? {
              ...transaction,
              statusLabel: 'View progress',
              isMuted: true,
              isComplete: true,
              completedSegments: transaction.totalSegments,
            }
          : transaction,
      street: listing?.street || 'Your home purchase',
      locality: [listing?.city, listing?.state, listing?.postalCode].filter(Boolean).join(', '),
      isActive: isActiveBuyerOffer(offer.transactionState),
    };
  });
